import React, { useMemo } from 'react';
import { orderBy } from 'lodash-es';
import type { MintSchedule } from '@soundxyz/sdk';
import type { Maybe } from '@soundxyz/utils/helpers';
import { hasSaleStarted } from '@components/Song/MintComponents/MintComponent/useMintState';
import {
  getScheduleId,
  hasSaleEnded,
  toMintTypeFromSchedule,
} from '@components/Song/MintComponents/helpers';
import { dropdownValueKey } from '@components/Song/MintComponents/shared-helpers';
import { quantityReducer } from '@components/main-components/inputs/QuantityInput';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { ModalContext } from '@context/modal';
import {
  CheckoutReleaseInfoFragmentDoc,
  type FragmentType,
  getFragment,
  gql,
} from '@graphql/reactQuery';
import {
  useEditionCollectableSchedules,
  useEditionMintScheduleWithEligibility,
} from '@store/sdk/reactQuery';
import type { PlaylistReferral } from '@utils/types';
import { ReleaseDetails } from '../shared';
import { BuyButtons } from './BuyButtons';
import { Panel } from './Panel';
import { PriceBreakdown } from './PriceBreakdown';
import { getDefaultSelectedSchedule, isMintSchedulePresale } from './helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment CheckoutReleaseInfo on Release {
    id
    chainId
    finalQuantity
    auctionContractType
    editionId
    title
    type
    contractAddress
    contract {
      id
      contractType
    }
    ...ReleaseDetails
    ...EditionCollectableSchedulesInfo
    ...ReleaseBuyButtons
  }
`);

type ActiveSchedule = {
  schedule: MintSchedule;
  key: Maybe<string>;
};

export const CheckoutModal = ({
  releaseData,
  totalMinted,
  playlistReferral,
}: {
  releaseData: FragmentType<CheckoutReleaseInfoFragmentDoc>;
  totalMinted: number;
  playlistReferral: PlaylistReferral | null;
}) => {
  const release = getFragment(CheckoutReleaseInfoFragmentDoc, releaseData);

  const { schedulesWithEligibility } = useEditionMintScheduleWithEligibility({
    release,
  });

  const { activeSchedulesWithEligibility } = useEditionCollectableSchedules({
    release,
  });

  const { chainId, id: releaseId, finalQuantity, contractAddress } = release;
  const { closeCancelModal } = ModalContext.useContainer();

  const schedulesList = useMemo((): typeof schedulesWithEligibility => {
    return orderBy(
      schedulesWithEligibility,
      // We always want to show presale first
      v => (isMintSchedulePresale(v.schedule) ? 1 : 0),
      'desc',
    );
  }, [schedulesWithEligibility]);

  const [selectedSchedule, setSelectedSchedule] = React.useState<ActiveSchedule | null>(() => {
    return getDefaultSelectedSchedule({
      schedules: activeSchedulesWithEligibility.map(({ schedule, hasSaleAccess }) => {
        return {
          schedule,
          hasSaleAccess: hasSaleAccess ?? false,
        };
      }),
    });
  });

  const [state, dispatch] = React.useReducer(quantityReducer, {
    value: 1,
    error: false,
  });

  const checkout = (
    <div className="flex flex-col gap-4">
      <ReleaseDetails releaseData={release} />

      <div className="flex flex-col gap-4">
        {schedulesList.map(({ hasSaleAccess, hasScheduleEligibility, schedule }) => {
          const saleStarted = hasSaleStarted({ startTimestamp: schedule.startTime });
          const saleEnded = hasSaleEnded({
            schedule,
            collected: schedule.totalMinted,
          });

          const panelKey = dropdownValueKey({
            type: toMintTypeFromSchedule(schedule),
            id: getScheduleId(schedule),
          });

          const isSelected = selectedSchedule?.key === panelKey && saleStarted && !!hasSaleAccess;

          return (
            <Panel
              key={`${schedule.interfaceId}-${panelKey}`}
              panelKey={panelKey}
              schedule={schedule}
              selected={isSelected}
              state={state}
              dispatch={dispatch}
              hasSaleAccess={!!hasSaleAccess}
              hasScheduleEligibility={!!hasScheduleEligibility}
              onClick={e => {
                e.stopPropagation();
                if (isSelected || !hasSaleAccess || !saleStarted || saleEnded) return;
                //reset state
                dispatch({ type: 'SET_INPUT', payload: 1 });
                setSelectedSchedule({
                  schedule,
                  key: panelKey,
                });
              }}
              releaseId={releaseId}
              chainId={chainId}
              saleStarted={saleStarted}
              rootReleaseTotalMinted={totalMinted}
              saleEnded={saleEnded}
              finalQuantity={finalQuantity ?? 0}
              editionAddress={contractAddress}
            />
          );
        })}

        <PriceBreakdown
          schedule={selectedSchedule?.schedule}
          quantity={state.value || 0}
          chainId={chainId}
          editionAddress={contractAddress}
        />
      </div>
    </div>
  );

  const ctaButtons = (
    <>
      {/* TODO: https://linear.app/sound/issue/ENG-7452/add-loading-states-to-buy-buttons*/}
      {selectedSchedule ? (
        <div className="w-full">
          <BuyButtons
            releaseData={release}
            schedule={selectedSchedule.schedule}
            error={state.error}
            quantity={state.value || 0}
            playlistReferral={playlistReferral}
          />
        </div>
      ) : null}
    </>
  );

  return (
    <StandardModal
      title="Checkout"
      body={checkout}
      closeModal={closeCancelModal}
      callToAction={ctaButtons}
      bodyWithoutPadding
    />
  );
};
